"use client";

import { useEffect, useState } from "react";

import Link from "next/link";

import { PageViewTracker } from "@/components/observability/PageViewTracker";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";

interface Props {
  params: Promise<{
    template: string;
  }>;
  searchParams: Promise<{
    download?: string;
  }>;
}

export default function EmailTemplatePage({ params, searchParams }: Props) {
  const [template, setTemplate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedParams, setResolvedParams] = useState<{
    template: string;
  } | null>(null);
  const [resolvedSearchParams, setResolvedSearchParams] = useState<{
    download?: string;
  } | null>(null);

  // Resolve params and searchParams
  useEffect(() => {
    async function resolveParams() {
      const resolved = await params;

      setResolvedParams(resolved);
    }
    void resolveParams();
  }, [params]);

  useEffect(() => {
    async function resolveSearchParams() {
      const resolved = await searchParams;

      setResolvedSearchParams(resolved);
    }
    void resolveSearchParams();
  }, [searchParams]);

  useEffect(() => {
    if (!resolvedParams) return;

    async function loadTemplate() {
      try {
        if (!resolvedParams) return;
        const response = await fetch(
          `/api/email-templates/${resolvedParams.template}`,
        );

        if (response.ok) {
          const html = await response.text();

          setTemplate(html);
        } else {
          setTemplate(null);
        }
      } catch (error) {
        console.error("Error loading email template:", error);
        setTemplate(null);
      } finally {
        setLoading(false);
      }
    }

    void loadTemplate();
  }, [resolvedParams]);

  useEffect(() => {
    // If download is requested, redirect to the static file
    if (resolvedSearchParams?.download === "true" && resolvedParams) {
      window.location.href = `/email-demos-seed-data/${resolvedParams.template}`;
    }
  }, [resolvedSearchParams?.download, resolvedParams]);

  if (loading || !resolvedParams) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Template Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The requested email template could not be found.
          </p>
          <Link
            className="text-blue-600 hover:text-blue-800 transition-colors"
            href={routes.public.emailDemos()}
          >
            ← Back to All Templates
          </Link>
        </div>
      </div>
    );
  }

  // Extract template name for title
  const templateName = resolvedParams.template
    .replace(/^\d{2}-/, "") // Remove number prefix
    .replace(".html", "")
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const handleDownload = () => {
    window.location.href = `/email-demos-seed-data/${resolvedParams.template}`;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                className="text-blue-600 hover:text-blue-800 transition-colors"
                href={routes.public.emailDemos()}
              >
                ← Back to All Templates
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-xl font-semibold text-gray-900">
                {templateName}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                type="button"
                onClick={handleDownload}
              >
                Download HTML
              </button>
              <button
                className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700 transition-colors print-button"
                type="button"
                onClick={handlePrint}
              >
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Full-size email template */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Template info bar */}
          <div className="bg-gray-50 px-4 py-2 border-b">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>File: {resolvedParams.template}</span>
              <span>Template: {templateName}</span>
            </div>
          </div>

          {/* Email template iframe */}
          <div className="p-4">
            <iframe
              className="w-full border rounded"
              srcDoc={template}
              style={{ height: "calc(100vh - 250px)", minHeight: "600px" }}
              title={`${templateName} Email Template`}
            />
          </div>
        </div>

        {/* Usage instructions */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            💡 Demo Instructions
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>
              • Use the <strong>Print</strong> button to create a PDF for
              presentations
            </li>
            <li>
              • Use the <strong>Download HTML</strong> button to save the
              template file
            </li>
            <li>
              • This template uses <strong>real seed data</strong> from the
              NeedBridge database
            </li>
            <li>
              • Features authentic organization names, volunteer profiles, and
              need descriptions
            </li>
            <li>• All templates are responsive and work on mobile devices</li>
          </ul>
        </div>
      </div>

      <PageViewTracker
        data={{ template: resolvedParams.template }}
        eventKey={EventKeys.PUBLIC_EMAIL_DEMO_TEMPLATE_PAGE_VIEW}
      />
    </div>
  );
}
